import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;
const SPLIT_JITTER = 10; // px — spreads the two children apart so they don't spawn fully coincident

/**
 * A melee-only chaser that, on death, spawns two weaker Skirmishers instead
 * of dying clean — the `minion:split` payload carries fully-formed child
 * configs (derived from this Splitter's own stats at construction, see
 * MinionWaveSystem._createMinion) so the listener (Game.js's _wireJuice,
 * next to its minion:exploded handler) doesn't need to know how to scale
 * them down itself. Punishes AoE-only clears — splash that one-shots a
 * Splitter still leaves two more to kill — and rewards focusing it down
 * with single-target damage instead.
 */
export class Splitter {
  constructor(eventBus, {
    x, y, radius = 7, hp, moveSpeed, contactDamage,
    childHp, childMoveSpeed, childContactDamage, childShotDamage, childShotSpeed, childFireInterval,
  }) {
    this.eventBus = eventBus;
    this.type = 'splitter';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this._childConfig = { hp: childHp, moveSpeed: childMoveSpeed, contactDamage: childContactDamage, shotDamage: childShotDamage, shotSpeed: childShotSpeed, fireInterval: childFireInterval };
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
      this.eventBus.emit('minion:split', {
        children: [
          { x: this.x - SPLIT_JITTER, y: this.y, ...this._childConfig },
          { x: this.x + SPLIT_JITTER, y: this.y, ...this._childConfig },
        ],
      });
    }
  }
}
